import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function Article() {
  const { t, i18n } = useTranslation();

  const tocItems = [
    'articleWhatIs',
    'articleHowWorks',
    'articleFindProducts',
    'articleReadSpreadsheet',
    'articleCategories',
    'articleSellerInfo',
    'articleQC',
    'articleWarehouse',
    'articleShipping',
    'articleSafety',
    'articleMistakes',
    'articleComparison',
    'articleFAQ',
  ];

  const relatedArticles = [
    { title: 'relatedReview', desc: 'reviewDesc' },
    { title: 'relatedShipping', desc: 'shippingCostDesc' },
    { title: 'relatedCoupon', desc: 'couponDesc' },
    { title: 'relatedWarehouse', desc: 'warehouseDesc' },
    { title: 'relatedLegit', desc: 'legitDesc' },
    { title: 'relatedQC', desc: 'whyQCDesc' },
  ];

  const safetyTips = [
    'safetyVerifyDomain',
    'safetyVerifyLinks',
    'safetyCheckSeller',
    'safetyReviewQC',
    'safetyConfirmPrice',
    'safetyConfirmShipping',
    'safetyNeverSharePassword',
    'safetyAvoidPayment',
    'safetyVerifyCoupons',
    'safetyCheckPolicies',
    'safetyBewareFake',
  ];

  const getArticleContent = () => {
    const lang = (i18n.language || 'en').toLowerCase().split('-')[0];
    if (lang === 'pt') return ptContent;
    if (lang === 'de') return deContent;
    return enContent;
  };

  const content = getArticleContent();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-green-600 transition-colors">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-700">{t('breadcrumbArticle')}</span>
          </nav>

          {/* Article Header */}
          <div className="mb-10">
            <div className="aspect-video rounded-2xl overflow-hidden mb-8 shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20colorful%20sneakers%20and%20hoodies%20organized%20in%20grid%20on%20warm%20cream%20background%20with%20a%20tablet%20showing%20spreadsheet%2C%20editorial%20product%20photography%2C%20soft%20natural%20light&width=1200&height=675&seq=article-hero-02&orientation=landscape&nocache=true"
                alt="CSSBuy Spreadsheet Complete Guide"
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('articleH1')}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
              <span>{t('articleAuthor')}</span>
              <span>•</span>
              <span>{t('articleMeta')}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {['CSSBuy Spreadsheet', 'CSSBuy Guide', 'CSSBuy Review', 'CSSBuy Shipping', 'CSSBuy Coupon'].map((tag) => (
                <span key={tag} className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Quick Answer */}
          <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg mb-10">
            <p className="text-gray-700 text-sm leading-relaxed">
              {content.quickAnswer}
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-10">
            <h3 className="font-semibold text-gray-900 mb-4">{t('articleToc')}</h3>
            <ol className="grid sm:grid-cols-2 gap-2">
              {tocItems.map((item, index) => (
                <li key={item}>
                  <a
                    href={`#${item}`}
                    className="text-sm text-green-600 hover:text-green-700 transition-colors"
                  >
                    {index + 1}. {t(item)}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <section id="articleWhatIs" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleWhatIs')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.whatIs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="mt-6 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Laptop%20screen%20showing%20organized%20product%20catalog%20with%20images%20next%20to%20a%20cup%20of%20coffee%20on%20light%20wood%20desk%2C%20warm%20ambient%20light%2C%20editorial%20workspace%20photography&width=800&height=450&seq=article-whatis-02&orientation=landscape&nocache=true"
                  alt="CSSBuy Spreadsheet Interface"
                  className="w-full h-auto object-cover"
                />
              </div>
            </section>

            <section id="articleHowWorks" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleHowWorks')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.howWorks.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>

            <section id="articleFindProducts" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleFindProducts')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.findProducts.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>

            <section id="articleReadSpreadsheet" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleReadSpreadsheet')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.readSpreadsheet.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>

            <section id="articleCategories" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleCategories')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.categories.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-6">
                {content.categoryList.map((cat, i) => (
                  <a
                    key={i}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 hover:border-green-500 hover:text-green-600 transition-colors text-center"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </section>

            <section id="articleSellerInfo" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleSellerInfo')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.sellerInfo.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>

            <section id="articleQC" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleQC')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.qc.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="mt-6 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Hands%20holding%20magnifying%20glass%20over%20sneaker%20sole%20inspecting%20stitching%20detail%20on%20clean%20surface%2C%20quality%20control%20inspection%20concept%2C%20soft%20lighting%2C%20editorial%20product%20photography&width=800&height=450&seq=article-qc-02&orientation=landscape&nocache=true"
                  alt="CSSBuy QC Photos"
                  className="w-full h-auto object-cover"
                />
              </div>
            </section>

            <section id="articleWarehouse" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleWarehouse')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.warehouse.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>

            <section id="articleShipping" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleShipping')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.shipping.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>

            <section id="articleSafety" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleSafety')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.safety.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="mt-6 bg-red-50 rounded-xl p-6">
                <h3 className="font-semibold text-red-800 mb-4">{t('safetyH1')}</h3>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {safetyTips.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-sm text-red-700">
                      <i className="ri-check-line mt-0.5 text-red-500" />
                      {t(tip)}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section id="articleMistakes" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleMistakes')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.mistakes.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>

            <section id="articleComparison" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleComparison')}</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                {content.comparison.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-left border-collapse bg-white rounded-lg shadow-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="py-4 px-4 font-semibold text-gray-900">{t('comparisonFeature')}</th>
                      <th className="py-4 px-4 font-semibold text-green-600 text-center">{t('comparisonCssbuy')}</th>
                      <th className="py-4 px-4 font-semibold text-gray-500 text-center">{t('comparisonAlternative')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['comparisonProductResearch', 'comparisonSpreadsheetResources', 'comparisonQCInfo', 'comparisonShippingGuide', 'comparisonWarehouseGuide', 'comparisonSafetyGuide'].map((row) => (
                      <tr key={row} className="border-b border-gray-100">
                        <td className="py-4 px-4 text-gray-700">{t(row)}</td>
                        <td className="py-4 px-4 text-center text-green-600 font-bold">{t('comparisonYes')}</td>
                        <td className="py-4 px-4 text-center text-green-600 font-bold">{t('comparisonYes')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="articleFAQ" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleFAQ')}</h2>
              <div className="space-y-4">
                {content.faq.map((item, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                    <p className="text-gray-600 text-sm">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Internal Link to Home */}
          <div className="mt-10 bg-green-50 rounded-xl p-6 border border-green-100">
            <p className="text-green-800 text-sm font-medium mb-2">{t('linkSpreadsheet')}</p>
            <p className="text-green-700 text-sm leading-relaxed mb-4">
              {content.backToHome}
            </p>
            <Link
              to="/"
              className="inline-flex items-center text-green-600 font-semibold hover:text-green-700 transition-colors"
            >
              {t('articleBackToHome')}
            </Link>
          </div>

          {/* Related Articles */}
          <div className="mt-10">
            <h3 className="text-xl font-bold text-gray-900 mb-6">{t('relatedArticles')}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedArticles.map((article, i) => (
                <Link
                  key={i}
                  to="/"
                  className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-green-200 transition-all"
                >
                  <h4 className="font-semibold text-gray-900 mb-2">{t(article.title)}</h4>
                  <p className="text-sm text-gray-600">{t(article.desc)}</p>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center bg-gray-900 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-white mb-4">{t('finalCtaTitle')}</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
              >
                {t('finalCtaPrimary')}
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30 whitespace-nowrap"
              >
                {t('finalCtaSecondary')}
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
              >
                {t('cssbuySpreadsheet')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
              >
                {t('buyItNow')}
              </a>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 text-center text-xs text-gray-500">
            {t('heroDisclaimer')}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

// Article content by language
const ptContent = {
  quickAnswer: 'O CSSBuy Spreadsheet é uma ferramenta de organização de recursos para compradores que desejam pesquisar produtos, comparar opções e acessar informações sobre envio, QC, armazém e cupons de forma estruturada e eficiente.',
  whatIs: [
    'O CSSBuy Spreadsheet é um recurso organizado que ajuda compradores a encontrar produtos, links e informações relacionadas ao CSSBuy. Ele funciona como uma central de informações onde você pode pesquisar categorias específicas de produtos, desde calçados e roupas até acessórios e eletrônicos.',
    'Ao contrário de sites de compras tradicionais, o CSSBuy Spreadsheet não vende produtos diretamente. Em vez disso, ele organiza links, categorias e recursos para que você possa fazer pesquisas mais eficientes antes de tomar decisões de compra.',
    'Este guia independente do CSSBuy Spreadsheet foi criado para ajudar compradores a navegar pelos recursos disponíveis com mais segurança e eficiência. Lembre-se de que este site é um recurso informativo independente e não representa oficialmente o CSSBuy.',
  ],
  howWorks: [
    'O CSSBuy Spreadsheet funciona organizando informações em categorias e subcategorias. Cada categoria contém links para produtos, vendedores e recursos relevantes. Os compradores podem navegar por essas categorias para encontrar o que procuram.',
    'A estrutura geralmente inclui colunas para nome do produto, categoria, link, preço, vendedor e notas adicionais. Isso permite uma comparação rápida entre diferentes opções.',
    'Ao usar o CSSBuy Spreadsheet, sempre verifique a data de atualização, confirme links diretamente e valide informações de vendedores antes de comprar.',
  ],
  findProducts: [
    'Para encontrar produtos no CSSBuy Spreadsheet, comece selecionando uma categoria relevante. As categorias mais populares incluem calçados, moletons, camisetas, jaquetas, calças, acessórios e mais.',
    'Use os filtros disponíveis para refinar sua pesquisa. Você pode filtrar por categoria, preço, tipo de produto ou outras características específicas.',
    'Sempre verifique o link do produto clicando nele para confirmar que ainda está ativo e que o preço está correto. Produtos e preços mudam frequentemente.',
  ],
  readSpreadsheet: [
    'Ler uma planilha CSSBuy requer atenção aos detalhes. Cada linha geralmente representa um produto ou recurso, enquanto as colunas contêm diferentes tipos de informação.',
    'Preste atenção especial às colunas de link, preço e vendedor. Estas são as informações mais importantes para sua decisão de compra.',
    'Verifique se há notas ou comentários sobre cada produto. Estas informações adicionais podem alertar sobre problemas conhecidos ou vantagens específicas.',
  ],
  categories: [
    'O CSSBuy Spreadsheet organiza produtos em categorias claras para facilitar a navegação. As principais categorias incluem: Calçados, Moletons/Casacos, Camisetas, Jaquetas, Calças/Shorts, Acessórios para Cabeça, Conjuntos, Roupa Íntima, Jerseys, Acessórios, Bolsas, Cintos, Perfumes e Outros Itens.',
    'Cada categoria contém subcategorias para refinar ainda mais sua pesquisa. Por exemplo, a categoria de Calçados pode incluir tênis, botas, sandálias e sapatos formais.',
  ],
  categoryList: [
    { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
    { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
    { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
    { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
    { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
    { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
    { name: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
    { name: 'Underwear', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
    { name: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
    { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
    { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
    { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
    { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
    { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
  ],
  sellerInfo: [
    'Informações sobre vendedores são cruciais para compras seguras. No CSSBuy Spreadsheet, verifique se há detalhes sobre reputação do vendedor, tempo de resposta e avaliações de outros compradores.',
    'Sempre pesquise o vendedor independentemente. Verifique se o link leva a uma página ativa e se o vendedor tem histórico de vendas positivo.',
    'Cuidado com vendedores que não têm informações de contato claras ou que solicitam pagamento fora das plataformas oficiais.',
  ],
  qc: [
    'QC (Quality Control) é um processo essencial onde fotos detalhadas do produto são tiradas antes do envio. Isso permite que você verifique se o produto corresponde às expectativas antes de pagar pelo envio.',
    'Ao revisar fotos de QC, preste atenção em detalhes como costura, materiais, cores, logotipos e qualidade geral. Compare com as fotos do produto na página do vendedor.',
    'Se o QC revelar problemas, você pode geralmente solicitar trocas ou reembolsos antes que o produto seja enviado. Aproveite este período para proteger seus interesses.',
  ],
  warehouse: [
    'O armazém CSSBuy recebe seus produtos, realiza inspeções de QC e os prepara para envio. Entender como o armazém funciona ajuda a planejar melhor suas compras.',
    'Produtos podem permanecer no armazém por um período limitado antes que taxas de armazenamento sejam aplicadas. Verifique as políticas atuais para evitar custos inesperados.',
    'Consolidação é um recurso útil onde vários pacotes podem ser combinados em um único envio, reduzindo custos de frete.',
  ],
  shipping: [
    'O custo de envio do CSSBuy depende de vários fatores: peso do pacote, dimensões, linha de envio escolhida e destino final. Use guias de estimativa para planejar seu orçamento.',
    'Diferentes linhas de envio oferecem diferentes equilíbrios entre velocidade e custo. Linhas mais rápidas geralmente custam mais, enquanto opções econômicas levam mais tempo.',
    'A consolidação de pacotes pode reduzir significativamente o custo total de envio, especialmente quando você compra múltiplos itens de diferentes vendedores.',
  ],
  safety: [
    'Segurança é a prioridade número um ao usar qualquer recurso de compra. Sempre verifique o domínio do site, valide links de produtos e confirme informações de vendedores antes de pagar.',
    'Nunca compartilhe suas senhas ou informações de pagamento com terceiros. Use apenas métodos de pagamento oficiais e protegidos.',
    'Verifique fotos de QC cuidadosamente antes de aprovar o envio. Uma vez que o produto é enviado, é muito mais difícil resolver problemas de qualidade.',
  ],
  mistakes: [
    'Um erro comum é não verificar a data de atualização da planilha. Links e preços mudam frequentemente, então informações desatualizadas podem levar a frustração.',
    'Outro erro é ignorar o QC. Muitos compradores aprovam o envio sem revisar as fotos de inspeção, o que pode resultar em receber produtos de qualidade inferior.',
    'Não consolidar pacotes quando possível é outro erro frequente. O envio de múltiplos pacotes separados quase sempre custa mais do que a consolidação.',
    'Finalmente, não pesquisar vendedores independentemente pode levar a problemas. Sempre verifique a reputação e histórico do vendedor antes de comprar.',
  ],
  comparison: [
    'Ao comparar opções de compra, considere não apenas o preço do produto, mas também o custo total incluindo envio, taxas e potenciais custos de armazém.',
    'O CSSBuy Spreadsheet é uma ferramenta de pesquisa, não uma plataforma de compra. Você ainda precisará usar o CSSBuy ou outros agentes de compra para realizar as transações.',
    'Compare diferentes linhas de envio, opções de QC e políticas de armazém para encontrar a melhor combinação para suas necessidades específicas.',
  ],
  faq: [
    { q: 'What is CSSBuy Spreadsheet?', a: 'É uma ferramenta organizacional de recursos para pesquisar produtos e categorias de forma estruturada.' },
    { q: 'How does CSSBuy Spreadsheet work?', a: 'Organiza links e informações em categorias para facilitar a pesquisa de produtos.' },
    { q: 'Is CSSBuy legit?', a: 'CSSBuy é um agente de compras conhecido. Sempre verifique informações independentemente.' },
    { q: 'How much does shipping cost?', a: 'Depende do peso, dimensões, linha de envio e destino. Use guias para estimar.' },
    { q: 'What is QC?', a: 'Quality Control são fotos de inspeção do produto antes do envio.' },
  ],
  backToHome: 'Volte para a página inicial do CSSBuy Spreadsheet para explorar mais recursos, guias e categorias de produtos. Nossa página principal contém links para todas as categorias principais, guias de envio, informações de cupons e muito mais.',
};

const deContent = {
  quickAnswer: 'Das CSSBuy Spreadsheet ist ein Ressourcenorganisationstool für Käufer, die Produkte recherchieren, Optionen vergleichen und strukturiert auf Informationen zu Versand, QC, Lager und Coupons zugreifen möchten.',
  whatIs: [
    'Das CSSBuy Spreadsheet ist eine organisierte Ressource, die Käufern hilft, Produkte, Links und Informationen im Zusammenhang mit CSSBuy zu finden. Es fungiert als Informationszentrale, in der Sie nach bestimmten Produktkategorien recherchieren können, von Schuhen und Kleidung bis hin zu Accessoires und Elektronik.',
    'Im Gegensatz zu traditionellen Einkaufswebsites verkauft das CSSBuy Spreadsheet keine Produkte direkt. Stattdessen organisiert es Links, Kategorien und Ressourcen, damit Sie effizienter recherchieren können, bevor Sie Kaufentscheidungen treffen.',
    'Diese unabhängige CSSBuy Spreadsheet-Anleitung wurde erstellt, um Käufern zu helfen, die verfügbaren Ressourcen sicherer und effizienter zu nutzen. Denken Sie daran, dass diese Website eine unabhängige Informationsressource ist und CSSBuy nicht offiziell vertritt.',
  ],
  howWorks: [
    'Das CSSBuy Spreadsheet funktioniert, indem es Informationen in Kategorien und Unterkategorien organisiert. Jede Kategorie enthält Links zu Produkten, Verkäufern und relevanten Ressourcen. Käufer können diese Kategorien durchsuchen, um zu finden, was sie suchen.',
    'Die Struktur umfasst normalerweise Spalten für Produktname, Kategorie, Link, Preis, Verkäufer und zusätzliche Notizen. Dies ermöglicht einen schnellen Vergleich zwischen verschiedenen Optionen.',
    'Bei der Verwendung des CSSBuy Spreadsheet überprüfen Sie immer das Aktualisierungsdatum, bestätigen Sie Links direkt und validieren Sie Verkäuferinformationen vor dem Kauf.',
  ],
  findProducts: [
    'Um Produkte im CSSBuy Spreadsheet zu finden, beginnen Sie mit der Auswahl einer relevanten Kategorie. Die beliebtesten Kategorien umfassen Schuhe, Hoodies, T-Shirts, Jacken, Hosen, Accessoires und mehr.',
    'Verwenden Sie die verfügbaren Filter, um Ihre Suche zu verfeinern. Sie können nach Kategorie, Preis, Produkttyp oder anderen spezifischen Merkmalen filtern.',
    'Überprüfen Sie immer den Produktlink, indem Sie darauf klicken, um zu bestätigen, dass er noch aktiv ist und der Preis korrekt ist. Produkte und Preise ändern sich häufig.',
  ],
  readSpreadsheet: [
    'Das Lesen eines CSSBuy Spreadsheet erfordert Aufmerksamkeit für Details. Jede Zeile stellt normalerweise ein Produkt oder eine Ressource dar, während die Spalten verschiedene Informationstypen enthalten.',
    'Achten Sie besonders auf die Spalten Link, Preis und Verkäufer. Dies sind die wichtigsten Informationen für Ihre Kaufentscheidung.',
    'Überprüfen Sie, ob es Notizen oder Kommentare zu jedem Produkt gibt. Diese zusätzlichen Informationen können auf bekannte Probleme oder spezifische Vorteile hinweisen.',
  ],
  categories: [
    'Das CSSBuy Spreadsheet organisiert Produkte in klare Kategorien für einfache Navigation. Die Hauptkategorien umfassen: Schuhe, Hoodies/Pullover, T-Shirts, Jacken, Hosen/Shorts, Kopfbedeckungen, Sets, Unterwäsche, Trikots, Accessoires, Taschen, Gürtel, Parfums und andere Artikel.',
    'Jede Kategorie enthält Unterkategorien, um Ihre Suche weiter zu verfeinern. Zum Beispiel kann die Kategorie Schuhe Turnschuhe, Stiefel, Sandalen und formelle Schuhe umfassen.',
  ],
  categoryList: [
    { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
    { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
    { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
    { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
    { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
    { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
    { name: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
    { name: 'Underwear', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
    { name: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
    { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
    { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
    { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
    { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
    { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
  ],
  sellerInfo: [
    'Verkäuferinformationen sind für sichere Einkäufe entscheidend. Im CSSBuy Spreadsheet überprüfen Sie, ob es Details zu Verkäuferreputation, Antwortzeit und Bewertungen anderer Käufer gibt.',
    'Recherchieren Sie den Verkäufer immer unabhängig. Überprüfen Sie, ob der Link zu einer aktiven Seite führt und ob der Verkäufer eine positive Verkaufshistorie hat.',
    'Seien Sie vorsichtig bei Verkäufern, die keine klaren Kontaktinformationen haben oder Zahlungen außerhalb offizieller Plattformen verlangen.',
  ],
  qc: [
    'QC (Quality Control) ist ein wesentlicher Prozess, bei dem detaillierte Produktfotos vor dem Versand aufgenommen werden. Dies ermöglicht es Ihnen, zu überprüfen, ob das Produkt Ihren Erwartungen entspricht, bevor Sie für den Versand bezahlen.',
    'Bei der Überprüfung von QC-Fotos achten Sie auf Details wie Nähte, Materialien, Farben, Logos und allgemeine Qualität. Vergleichen Sie mit den Produktfotos auf der Verkäuferseite.',
    'Wenn das QC Probleme aufdeckt, können Sie normalerweise Umtausch oder Rückerstattung anfordern, bevor das Produkt versendet wird. Nutzen Sie diese Zeit, um Ihre Interessen zu schützen.',
  ],
  warehouse: [
    'Das CSSBuy-Lager empfängt Ihre Produkte, führt QC-Inspektionen durch und bereitet sie für den Versand vor. Das Verständnis der Lagerfunktionen hilft Ihnen, Ihre Einkäufe besser zu planen.',
    'Produkte können für eine begrenzte Zeit im Lager bleiben, bevor Lagergebühren anfallen. Überprüfen Sie die aktuellen Richtlinien, um unerwartete Kosten zu vermeiden.',
    'Konsolidierung ist eine nützliche Funktion, bei der mehrere Pakete zu einem einzigen Versand zusammengefasst werden können, was die Versandkosten senkt.',
  ],
  shipping: [
    'Die CSSBuy-Versandkosten hängen von mehreren Faktoren ab: Paketgewicht, Abmessungen, gewählte Versandlinie und Endziel. Verwenden Sie Schätzleitfäden, um Ihr Budget zu planen.',
    'Verschiedene Versandlinien bieten unterschiedliche Gleichgewichte zwischen Geschwindigkeit und Kosten. Schnellere Linien kosten normalerweise mehr, während wirtschaftliche Optionen länger dauern.',
    'Die Konsolidierung von Paketen kann die Gesamtversandkosten erheblich senken, insbesondere wenn Sie mehrere Artikel von verschiedenen Verkäufern kaufen.',
  ],
  safety: [
    'Sicherheit hat oberste Priorität bei der Nutzung jeder Einkaufsressource. Überprüfen Sie immer die Website-Domain, validieren Sie Produktlinks und bestätigen Sie Verkäuferinformationen vor der Zahlung.',
    'Teilen Sie niemals Ihre Passwörter oder Zahlungsinformationen mit Dritten. Verwenden Sie nur offizielle und geschützte Zahlungsmethoden.',
    'Überprüfen Sie QC-Fotos sorgfältig, bevor Sie den Versand genehmigen. Sobald das Produkt versendet wurde, ist es viel schwieriger, Qualitätsprobleme zu lösen.',
  ],
  mistakes: [
    'Ein häufiger Fehler ist, das Aktualisierungsdatum der Tabelle nicht zu überprüfen. Links und Preise ändern sich häufig, daher können veraltete Informationen zu Frustration führen.',
    'Ein weiterer Fehler ist, QC zu ignorieren. Viele Käufer genehmigen den Versand ohne Überprüfung der Inspektionsfotos, was dazu führen kann, Produkte minderwertiger Qualität zu erhalten.',
    'Pakete nicht zu konsolidieren, wenn möglich, ist ein weiterer häufiger Fehler. Der Versand mehrerer separater Pakete kostet fast immer mehr als die Konsolidierung.',
    'Schließlich kann das Nichtrecherchieren von Verkäufern zu Problemen führen. Überprüfen Sie immer Ruf und Historie des Verkäufers vor dem Kauf.',
  ],
  comparison: [
    'Beim Vergleich von Einkaufsoptionen berücksichtigen Sie nicht nur den Produktpreis, sondern auch die Gesamtkosten einschließlich Versand, Gebühren und potenziellen Lagerkosten.',
    'Das CSSBuy Spreadsheet ist ein Recherche-Tool, keine Einkaufsplattform. Sie müssen immer noch CSSBuy oder andere Einkaufsagenten verwenden, um Transaktionen durchzuführen.',
    'Vergleichen Sie verschiedene Versandlinien, QC-Optionen und Lagerrichtlinien, um die beste Kombination für Ihre spezifischen Bedürfnisse zu finden.',
  ],
  faq: [
    { q: 'What is CSSBuy Spreadsheet?', a: 'Es ist ein Ressourcenorganisationstool zum strukturierten Recherchieren von Produkten und Kategorien.' },
    { q: 'How does CSSBuy Spreadsheet work?', a: 'Es organisiert Links und Informationen in Kategorien zur Erleichterung der Produktsuche.' },
    { q: 'Is CSSBuy legit?', a: 'CSSBuy ist ein bekannter Einkaufsagent. Überprüfen Sie immer Informationen unabhängig.' },
    { q: 'How much does shipping cost?', a: 'Hängt von Gewicht, Abmessungen, Versandlinie und Ziel ab. Verwenden Sie Leitfäden zur Schätzung.' },
    { q: 'What is QC?', a: 'Quality Control sind Inspektionsfotos des Produkts vor dem Versand.' },
  ],
  backToHome: 'Kehren Sie zur CSSBuy Spreadsheet-Startseite zurück, um mehr Ressourcen, Anleitungen und Produktkategorien zu erkunden. Unsere Hauptseite enthält Links zu allen Hauptkategorien, Versandanleitungen, Coupon-Informationen und mehr.',
};

const enContent = {
  quickAnswer: 'The CSSBuy Spreadsheet is a resource organization tool for buyers who want to research products, compare options, and access structured information about shipping, QC, warehouse, and coupons efficiently.',
  whatIs: [
    'The CSSBuy Spreadsheet is an organized resource that helps buyers find products, links, and information related to CSSBuy. It serves as an information hub where you can search for specific product categories, from shoes and clothing to accessories and electronics.',
    'Unlike traditional shopping websites, the CSSBuy Spreadsheet does not sell products directly. Instead, it organizes links, categories, and resources so you can do more efficient research before making purchase decisions.',
    'This independent CSSBuy Spreadsheet guide was created to help buyers navigate available resources more safely and efficiently. Remember that this site is an independent informational resource and does not officially represent CSSBuy.',
  ],
  howWorks: [
    'The CSSBuy Spreadsheet works by organizing information into categories and subcategories. Each category contains links to products, sellers, and relevant resources. Buyers can browse these categories to find what they are looking for.',
    'The structure typically includes columns for product name, category, link, price, seller, and additional notes. This allows for quick comparison between different options.',
    'When using the CSSBuy Spreadsheet, always check the update date, confirm links directly, and validate seller information before purchasing.',
  ],
  findProducts: [
    'To find products in the CSSBuy Spreadsheet, start by selecting a relevant category. The most popular categories include shoes, hoodies, t-shirts, jackets, pants, accessories, and more.',
    'Use available filters to refine your search. You can filter by category, price, product type, or other specific characteristics.',
    'Always verify the product link by clicking it to confirm it is still active and the price is correct. Products and prices change frequently.',
  ],
  readSpreadsheet: [
    'Reading a CSSBuy Spreadsheet requires attention to detail. Each row typically represents a product or resource, while columns contain different types of information.',
    'Pay special attention to the link, price, and seller columns. These are the most important pieces of information for your purchase decision.',
    'Check if there are notes or comments about each product. This additional information can alert you to known issues or specific advantages.',
  ],
  categories: [
    'The CSSBuy Spreadsheet organizes products into clear categories for easy navigation. Main categories include: Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear, Jerseys, Accessories, Bags, Belts, Perfumes, and Other Items.',
    'Each category contains subcategories to further refine your search. For example, the Shoes category may include sneakers, boots, sandals, and formal shoes.',
  ],
  categoryList: [
    { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
    { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
    { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
    { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
    { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
    { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
    { name: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
    { name: 'Underwear', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
    { name: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
    { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
    { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
    { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
    { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
    { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
  ],
  sellerInfo: [
    'Seller information is crucial for safe purchases. In the CSSBuy Spreadsheet, check if there are details about seller reputation, response time, and reviews from other buyers.',
    'Always research the seller independently. Verify that the link leads to an active page and that the seller has a positive sales history.',
    'Be cautious of sellers who do not have clear contact information or who request payment outside official platforms.',
  ],
  qc: [
    'QC (Quality Control) is an essential process where detailed photos of the product are taken before shipping. This allows you to verify that the product meets expectations before paying for shipping.',
    'When reviewing QC photos, pay attention to details like stitching, materials, colors, logos, and overall quality. Compare with the product photos on the seller page.',
    'If QC reveals problems, you can generally request exchanges or refunds before the product is shipped. Take advantage of this period to protect your interests.',
  ],
  warehouse: [
    'The CSSBuy warehouse receives your products, performs QC inspections, and prepares them for shipping. Understanding how the warehouse works helps you plan your purchases better.',
    'Products can remain in the warehouse for a limited time before storage fees apply. Check current policies to avoid unexpected costs.',
    'Consolidation is a useful feature where multiple packages can be combined into a single shipment, reducing shipping costs.',
  ],
  shipping: [
    'CSSBuy shipping cost depends on several factors: package weight, dimensions, chosen shipping line, and final destination. Use estimation guides to plan your budget.',
    'Different shipping lines offer different balances between speed and cost. Faster lines generally cost more, while economical options take longer.',
    'Package consolidation can significantly reduce total shipping costs, especially when you purchase multiple items from different sellers.',
  ],
  safety: [
    'Safety is the number one priority when using any shopping resource. Always verify the website domain, validate product links, and confirm seller information before paying.',
    'Never share your passwords or payment information with third parties. Use only official and protected payment methods.',
    'Check QC photos carefully before approving shipping. Once the product is shipped, it is much more difficult to resolve quality issues.',
  ],
  mistakes: [
    'A common mistake is not checking the spreadsheet update date. Links and prices change frequently, so outdated information can lead to frustration.',
    'Another mistake is ignoring QC. Many buyers approve shipping without reviewing inspection photos, which can result in receiving inferior quality products.',
    'Not consolidating packages when possible is another frequent error. Shipping multiple separate packages almost always costs more than consolidation.',
    'Finally, not researching sellers independently can lead to problems. Always verify the reputation and history of the seller before buying.',
  ],
  comparison: [
    'When comparing shopping options, consider not only the product price but also the total cost including shipping, fees, and potential warehouse costs.',
    'The CSSBuy Spreadsheet is a research tool, not a shopping platform. You will still need to use CSSBuy or other buying agents to complete transactions.',
    'Compare different shipping lines, QC options, and warehouse policies to find the best combination for your specific needs.',
  ],
  faq: [
    { q: 'What is CSSBuy Spreadsheet?', a: 'It is a resource organization tool for structured product and category research.' },
    { q: 'How does CSSBuy Spreadsheet work?', a: 'It organizes links and information into categories to facilitate product research.' },
    { q: 'Is CSSBuy legit?', a: 'CSSBuy is a known buying agent. Always verify information independently.' },
    { q: 'How much does shipping cost?', a: 'Depends on weight, dimensions, shipping line and destination. Use guides to estimate.' },
    { q: 'What is QC?', a: 'Quality Control are inspection photos of the product before shipping.' },
  ],
  backToHome: 'Return to the CSSBuy Spreadsheet homepage to explore more resources, guides, and product categories. Our main page contains links to all major categories, shipping guides, coupon information, and more.',
};